// Helpers that pull structured exports out of the rewriter's optimized HTML.
// Used by ContentTab's page-detail drawer and the audit Fix modals.
// All return { count, ... } so callers can branch on count === 0.

export function extractJsonLd(html) {
  if (!html) return { count: 0, types: [], scriptsText: '' };
  const blocks =
    html.match(/<script[^>]*type=["']application\/ld\+json["'][^>]*>[\s\S]*?<\/script>/gi) || [];
  const types = [];
  for (const block of blocks) {
    const body = block
      .replace(/<script[^>]*>/i, '')
      .replace(/<\/script>/i, '')
      .trim();
    try {
      const parsed = JSON.parse(body);
      const t = parsed['@type'];
      if (Array.isArray(t)) types.push(...t.filter((x) => typeof x === 'string'));
      else if (typeof t === 'string') types.push(t);
    } catch {
      // unparseable block — still copy it, just can't name the type
    }
  }
  return {
    count: blocks.length,
    types: [...new Set(types)],
    scriptsText: blocks.join('\n\n'),
  };
}

// Pull <faqsection><question>Q</question><answer>A</answer></faqsection>
// blocks out of the rewriter's output and re-emit them as standard
// semantic HTML the customer can paste into a page template — no custom
// tags, no CSS prerequisites, renders cleanly in any browser, and
// satisfies the audit's FAQ heading signal.
export function extractFaqHtml(html) {
  if (!html) return { count: 0, html: '' };
  const blocks = html.match(/<faqsection[^>]*>[\s\S]*?<\/faqsection>/gi) || [];
  const items = [];
  for (const block of blocks) {
    const q = (block.match(/<question[^>]*>([\s\S]*?)<\/question>/i) || [])[1];
    const a = (block.match(/<answer[^>]*>([\s\S]*?)<\/answer>/i) || [])[1];
    if (q && a) {
      items.push({
        question: q.trim().replace(/\s+/g, ' '),
        answer: a.trim().replace(/\s+/g, ' '),
      });
    }
  }
  if (!items.length) return { count: 0, html: '' };
  const body = items
    .map(({ question, answer }) => `  <h3>${question}</h3>\n  <p>${answer}</p>`)
    .join('\n\n');
  return {
    count: items.length,
    html: `<section class="faq" id="faq">\n  <h2>Frequently Asked Questions</h2>\n\n${body}\n</section>`,
  };
}

// Same idea for <answercapsule><claim>...</claim><context>...</context>
// </answercapsule> — emit a <blockquote class="answer-capsule"> per
// capsule, which the audit also accepts.
export function extractAnswerCapsules(html) {
  if (!html) return { count: 0, html: '' };
  const blocks = html.match(/<answercapsule[^>]*>[\s\S]*?<\/answercapsule>/gi) || [];
  const items = [];
  for (const block of blocks) {
    const claim = (block.match(/<claim[^>]*>([\s\S]*?)<\/claim>/i) || [])[1];
    const context = (block.match(/<context[^>]*>([\s\S]*?)<\/context>/i) || [])[1];
    if (claim) {
      items.push({
        claim: claim.trim().replace(/\s+/g, ' '),
        context: (context || '').trim().replace(/\s+/g, ' '),
      });
    }
  }
  if (!items.length) return { count: 0, html: '' };
  const body = items
    .map(({ claim, context }) =>
      context
        ? `<blockquote class="answer-capsule">\n  <strong>${claim}</strong>\n  <span>${context}</span>\n</blockquote>`
        : `<blockquote class="answer-capsule">\n  <strong>${claim}</strong>\n</blockquote>`,
    )
    .join('\n\n');
  return { count: items.length, html: body };
}
